import {
  actionColumn,
  ButtonMenu,
  column,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridSortConfig,
  GridTableLayout,
  multiFilter,
  PageHeaderLayout,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  toggleFilter,
  useGridTableLayoutState,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  FeatureFlagType,
  LotType,
  Order,
  ProjectFeature,
  ProjectFeatureDetail,
  ProjectFilter,
  ProjectOrder,
} from "src/generated/graphqlTypes";
import { ProjectFeaturesCell } from "src/routes/admin/components/ProjectFeaturesCell";
import { ProjectStatusCell } from "src/routes/admin/components/ProjectStatusCell";
import { QbIdModal } from "src/routes/admin/components/QbIdModal";
import {
  AdminProjectsFilterDataQuery,
  ProjectAdminFragment,
  ProjectsQuery,
  useAdminProjectsFilterDataQuery,
  useProjectsQuery,
  useSetProjectIsTestMutation,
} from "src/routes/admin/projects/Admin.generated";
import {
  keyBy,
  projectStatusDetails,
  queryResult,
  removeTag,
  safeEntries,
  sortBy,
  stageCodeToNameMapper,
} from "src/utils";
import { parseOrder, toOrder } from "../../../utils/ordering";

export function AdminProjects() {
  const query = useAdminProjectsFilterDataQuery({ fetchPolicy: "cache-first" });
  return queryResult(query, (data) => <AdminProjectsView metadata={data} />);
}

function AdminProjectsView({ metadata }: { metadata: AdminProjectsFilterDataQuery }) {
  const { cohorts, developments, markets, projectFeatures } = metadata;
  const [orderBy, setOrderBy] = useState<ProjectOrder>({ id: Order.Asc });
  const isReadonly = !useFeatureFlag(FeatureFlagType.BlueprintAdmin);
  const filterDefs: FilterDefs<ProjectFilter> = useMemo(() => {
    const latestActiveStage = multiFilter({
      options: safeEntries(stageCodeToNameMapper),
      getOptionValue: ([code]) => code,
      getOptionLabel: ([, name]) => name,
    });

    const market = multiFilter({ options: markets, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });

    const status = multiFilter({
      options: safeEntries(projectStatusDetails),
      getOptionValue: ([code]) => code,
      getOptionLabel: ([_code, name]) => name,
    });
    const development = multiFilter({
      options: developments,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const cohort = multiFilter({
      options: sortBy(cohorts, ({ name }) => name),
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const lotType = multiFilter({
      options: [LotType.Bool, LotType.Boyl, LotType.Hbl, LotType.Btr, LotType.Fee],
      getOptionValue: (v) => v,
      getOptionLabel: (v) => v,
    });

    const isTest = toggleFilter({ label: "Only show test projects" });

    return { latestActiveStage, market, status, development, cohort, lotType, isTest };
  }, [markets, developments, cohorts]);

  const layoutState = useGridTableLayoutState<ProjectFilter>({
    persistedFilter: { filterDefs, storageKey: "projectFilter" },
    search: "server",
  });

  const query = useProjectsQuery({
    variables: {
      filter: { ...layoutState.filter, search: layoutState.searchString ?? "" },
      page: { limit: 100, offset: 0 },
      orderBy,
    },
  });

  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.projectsPage.pageInfo.hasNextPage) {
      await query.fetchMore({ variables: { page: { offset: query.data?.projectsPage.entities?.length, limit: 100 } } });
    }
  }, [query]);

  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );

  const featureCodeToName = keyBy(
    projectFeatures,
    (f) => f.code,
    (f) => f.name,
  );

  const columns = useMemo(
    () => createColumns(featureCodeToName, projectFeatures, isReadonly),
    [featureCodeToName, projectFeatures, isReadonly],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Mission Control \u{1F680}" }}>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          sorting,
          query,
          columns,
          createRows,
          style: { bordered: true },
          as: "virtual",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
          emptyFallback: "No projects found that match the given filters.",
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<ProjectAdminFragment>;

function createRows(data: ProjectsQuery | undefined): GridDataRow<Row>[] {
  const projects = data?.projectsPage.entities ?? [];
  return [simpleHeader, ...projects.map((p) => ({ kind: "data" as const, id: p.id, data: p }))];
}

function createColumns(
  featureCodeToName: Record<ProjectFeature, string>,
  features: ProjectFeatureDetail[],
  isReadonly: boolean,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: () => "ID",
      data: ({ id }) => ({ content: () => id, value: removeTag(id) }),
      w: "80px",
      serverSideSortKey: "id",
    }),
    column<Row>({
      header: () => "Name",
      data: ({ name, isTest }) => ({
        content: () => {
          return (
            <div css={Css.df.aic.gap1.$}>
              <span>{name}</span>
              {isTest && <Tag text="Test" type="info" />}
            </div>
          );
        },
        value: `${name} ${isTest ? "Test" : ""}`,
      }),
      mw: "360px",
      serverSideSortKey: "buildAddress",
    }),
    column<Row>({
      header: () => "Status",
      data: (project) => ({
        content: () => <ProjectStatusCell project={project} isReadonly={isReadonly} />,
        value: project.status.name,
      }),
      w: "160px",
      serverSideSortKey: "status",
    }),
    column<Row>({
      header: () => "Stage",
      data: ({ latestActiveStage }) => latestActiveStage?.stage.name,
      w: "140px",
      serverSideSortKey: "latestActiveStage",
    }),
    column<Row>({
      header: () => "Market",
      data: ({ market }) => market?.name,
      w: "160px",
      serverSideSortKey: "market",
    }),
    column<Row>({
      header: () => "Homeowner",
      data: ({ primaryHomeowner }) => primaryHomeowner?.shortName,
      w: "180px",
      serverSideSortKey: "homeowner",
    }),
    column<Row>({
      header: () => "Features",
      data: (project) => ({
        content: () => <ProjectFeaturesCell project={project} features={features} isReadonly={isReadonly} />,
        value: project.features.map((f) => featureCodeToName[f]).join(" "),
      }),
      w: "360px",
    }),
    actionColumn<Row>({
      header: () => "Actions",
      data: (project) => (!isReadonly ? <ProjectActionsMenu project={project} /> : undefined),
      w: "80px",
      clientSideSort: false,
    }),
  ];
}

type ProjectActionsMenuProps = { project: ProjectAdminFragment };

function ProjectActionsMenu({ project }: ProjectActionsMenuProps) {
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [projectIsTestMutation] = useSetProjectIsTestMutation();

  const toggleTest = useCallback(async () => {
    await projectIsTestMutation({ variables: { projectId: project.id, isTest: !project.isTest } });

    triggerNotice({
      icon: "success",
      message: `${project.name} (${project.id}) ${!project.isTest ? "marked" : "unmarked"} as Test Project`,
    });
  }, [triggerNotice, projectIsTestMutation, project]);

  return (
    <ButtonMenu
      trigger={{ icon: "verticalDots" }}
      placement="right"
      items={[
        { label: project.isTest ? "Unmark as test" : "Mark as test", onClick: toggleTest },
        { label: "Edit QBO ID", onClick: () => openModal({ content: <QbIdModal project={project} /> }) },
      ]}
    />
  );
}
